import { DatePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialAdminControllerService,
  ItauCobrancaRecorrenteResponse,
  ItauRecorrenciaResponse
} from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-cobrancas-recorrentes',
  templateUrl: './cobrancas-recorrentes.component.html',
  styleUrl: './cobrancas-recorrentes.component.scss',
  providers: [ConfirmationService, MessageService, DatePipe]
})
export class CobrancasRecorrentesComponent implements OnInit {
  financialAdminService = inject(FinancialAdminControllerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private datePipe = inject(DatePipe);

  @Input() recurrence: ItauRecorrenciaResponse;

  colsCob = [
    new TableColumn.Builder()
      .setHeader('Criação')
      .setField('calendario.criacao')
      .setType('date')
      .setCondition('between')
      .setRequired(true)
      .setDisplayFunction((cobranca: ItauCobrancaRecorrenteResponse) =>
        this.datePipe.transform(
          cobranca?.atualizacao?.find(
            (a) => a.status === ItauCobrancaRecorrenteResponse.StatusEnum.CRIADA
          )?.data || cobranca.calendario?.criacao,
          'dd/MM/yyyy HH:mm:ss'
        )
      )
      .build(),
    new TableColumn.Builder().setHeader('TxId').setField('txid').build(),
    new TableColumn.Builder().setHeader('IdRec').setField('idRec').build(),
    new TableColumn.Builder()
      .setHeader('Email')
      .setField('devedor.email')
      .build(),
    new TableColumn.Builder()
      .setHeader('Vencimento')
      .setField('calendario.dataDeVencimento')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((cobranca: ItauCobrancaRecorrenteResponse) =>
        this.datePipe.transform(
          cobranca.calendario?.dataDeVencimento,
          'dd/MM/yyyy'
        )
      )
      .build(),
    new TableColumn.Builder().setHeader('Status').setField('status').build(),
    new TableColumn.Builder()
      .setHeader('Valor')
      .setField('valor.original')
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Retentativa')
      .setField('politicaRetentativa')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField('')
      .setType('button')
      .build()
  ];

  attemptCols = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dataLiquidacao')
      .setType('date')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('EndToEnd')
      .setField('endToEndId')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo')
      .setFilter(false)
      .setField('tipo')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setFilter(false)
      .setField('status')
      .build()
  ];

  dropdownFiltersCob = {
    status: Object.keys(ItauCobrancaRecorrenteResponse.StatusEnum)
      .map((key) => ({
        label: key,
        value: ItauCobrancaRecorrenteResponse.StatusEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }]),
    politicaRetentativa: Object.keys(
      ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum
    )
      .map((key) => ({
        label: ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum[key],
        value: ItauCobrancaRecorrenteResponse.PoliticaRetentativaEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }])
  };

  defaultFilters = {
    'calendario.criacao': [...this.startRange],
    status: null
  };
  cobrancas: ItauCobrancaRecorrenteResponse[];
  askCancelCharge = (
    cobranca: ItauCobrancaRecorrenteResponse,
    $event: MouseEvent
  ) => {
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Cancelar',
      rejectLabel: 'Manter',
      rejectButtonStyleClass: 'p-button-primary',
      message: `Tem certeza que deseja cancelar a cobrança "${cobranca.txid}"?`,
      header: 'Confirmação',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      target: $event.target,
      accept: async () => await this.cancelCharge(cobranca)
    });
  };
  buttons = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.TIMES_CIRCLE)
      .setTooltip('Cancelar cobrança')
      .setTooltipPosition('bottom')
      .setSeverity('danger')
      .setAction(this.askCancelCharge)
      .setDisplay(
        (c: ItauCobrancaRecorrenteResponse) =>
          c.status === ItauCobrancaRecorrenteResponse.StatusEnum.ATIVA &&
          (!c.calendario?.dataDeVencimento ||
            (c.calendario.dataDeVencimento as any).getTime() > Date.now())
      )
      .setText(true)
      .build()
  ];

  async ngOnInit() {
    if (!this.recurrence) await this.findCobrancas();
    else {
      this.defaultFilters['calendario.criacao'] = [];
      this.cobrancas =
        this.recurrence.cobsr?.map((c) => this.mapDates(c)) || [];
    }
  }

  async findCobrancas(range?: Date[]) {
    try {
      LoaderService.showLoader();
      this.cobrancas = await lastValueFrom(
        this.financialAdminService
          .findCobrancasRecorrentes(
            (range || this.startRange)?.at(0)?.toISOString(),
            (range || this.startRange)?.at(1)?.toISOString(),
            this.recurrence?.idRec
          )
          .pipe(
            map((data) =>
              data.result.map((r: ItauCobrancaRecorrenteResponse) =>
                this.mapDates(r)
              )
            )
          )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async cancelCharge(cobranca: ItauCobrancaRecorrenteResponse) {
    if (cobranca.status === ItauCobrancaRecorrenteResponse.StatusEnum.ATIVA) {
      try {
        LoaderService.showLoader();
        cobranca = await lastValueFrom(
          this.financialAdminService
            .cancelRecurrentCharge(cobranca.txid)
            .pipe(map((data) => data.result))
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Cobrança cancelada com sucesso'
        });
        this.cobrancas
          ?.filter((c) => c.txid === cobranca.txid)
          .forEach((c) => (c.status = cobranca.status));
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  mapDates(r: ItauCobrancaRecorrenteResponse) {
    if (r.calendario?.criacao)
      r.calendario.criacao = FormUtil.dateUTC(r.calendario.criacao) as any;
    if (r.calendario?.dataDeVencimento)
      r.calendario.dataDeVencimento = FormUtil.dateUTC(
        r.calendario.dataDeVencimento
      ) as any;
    return r;
  }

  get startRange() {
    const date = new Date(new Date().setDate(new Date().getDate() - 1));
    date.setHours(0, 0, 0, 0);
    return [date, new Date()];
  }
}
